import type { OperationFailureReason } from '@mint-lib/datadog';

import {
  type ConnectionOperationContext,
  failConnectionOperation,
  startConnectionOperation,
  succeedConnectionOperation,
} from './connectionOperation.js';

export type ConnectionReconnectOperationContext = ConnectionOperationContext;

/**
 * Starts the `connection.reconnect` RUM operation when the user reconnects an
 * existing connection: on the Reconnect click for OAuth, on the credentials
 * form submit for basic auth. See {@link startConnectionOperation} for how it
 * survives the OAuth redirect and stays apart from `connection.add`.
 */
export function startConnectionReconnectOperation(
  context: ConnectionReconnectOperationContext,
) {
  startConnectionOperation('connection.reconnect', context);
}

/** Ends the open `connection.reconnect` operation, if any, as succeeded. */
export function succeedConnectionReconnectOperation() {
  succeedConnectionOperation('connection.reconnect');
}

/** Ends the open `connection.reconnect` operation, if any, as failed. */
export function failConnectionReconnectOperation(
  reason: OperationFailureReason,
) {
  failConnectionOperation('connection.reconnect', reason);
}

// `invalid_profile` (CM360) and `invalid_advertiser` (AdForm): the provider
// accepted the user and the token was refreshed, but the connection is still
// unusable until the user picks a new profile, or at all for AdForm. That is
// not a success, since the user did not get a working connection back. It is
// not an `error` either: nothing broke, and counting it there would hide real
// OAuth failures in the error rate. Datadog's `other` keeps these countable on
// their own.
const RECONFIGURATION_RESULTS: ReadonlySet<string> = new Set([
  'invalid_profile',
  'invalid_advertiser',
]);

/**
 * Ends the open `connection.reconnect` operation, if any, from a non-success
 * `oauth_result`: `other` when the reconnect worked but the connection needs
 * attention, `error` otherwise.
 */
export function failConnectionReconnectOperationForOAuthResult(
  oauthResult: string,
) {
  failConnectionReconnectOperation(
    RECONFIGURATION_RESULTS.has(oauthResult) ? 'other' : 'error',
  );
}
